import { batch } from 'react-redux';

import { ausFindingsApi, ausSubmitApi } from 'app/services/api';
import _ from 'lodash';

import {
  AusOperationStatus,
  AusOperationStatusToNumberStatusMap,
  AusTypeSwitchStatus,
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
} from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

import {
  getLoanApplicationCommonDataByApplicationIdAction,
  updateAusRelatedDataAction,
  updateRootAusRelatedFieldsAction,
} from '../actions';
import { selectAusSubmitOperations, selectDealFromApplication } from '../selectors';

// A casefile-lookup submit has no pending changes to reload. `None` computes
// WithPendingChanges=false server-side (an `All` scope's WithPendingChanges is a computed
// property that is always true regardless of the posted JSON), so the terminal SignalR event
// clears the handler's All+withPendingChanges early-return and is actually processed.
const WHOLESALE_AUS_LOAD_SCOPE = { type: 'None' };

export const AUS_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/aus`,
  {
    ADD_NEW_AUS_SUBMIT_OPERATION: 'ADD_NEW_AUS_SUBMIT_OPERATION',
    GET_AUS_SUMMARY_DATA_PENDING_STATUS: 'GET_AUS_SUMMARY_DATA_PENDING_STATUS',
    GET_AUS_SUMMARY_DATA_REJECTED_STATUS: 'GET_AUS_SUMMARY_DATA_REJECTED_STATUS',
    GET_AUS_SUMMARY_DATA: 'GET_AUS_SUMMARY_DATA',
    GET_AUS_SUBMIT_OPERATIONS: 'GET_AUS_SUBMIT_OPERATIONS',
    GET_AUS_FINDINGS_LIST: 'GET_AUS_FINDINGS_LIST',
    RESET_AUS_FINDINGS_LIST: 'RESET_AUS_FINDINGS_LIST',
    SWITCH_AUS_TYPE_PENDING_STATUS: 'SWITCH_AUS_TYPE_PENDING_STATUS',
    SWITCH_AUS_TYPE_FULFILLED_STATUS: 'SWITCH_AUS_TYPE_FULFILLED_STATUS',
    SET_AUS_CASEFILE_INTAKE: 'SET_AUS_CASEFILE_INTAKE',
    SET_AUS_MISMO_PREFILL: 'SET_AUS_MISMO_PREFILL',
  },
);

const setAusCasefileIntakeAction = ({ duCasefileId, lpAusKey }) => ({
  type: AUS_ACTION_TYPE.SET_AUS_CASEFILE_INTAKE,
  payload: { duCasefileId: duCasefileId ?? null, lpAusKey: lpAusKey ?? null },
});

// The MISMO-detected IDs, kept apart from the working casefile-intake IDs (which the user
// can overwrite on submit) so the "From MISMO import" chip only ever attaches to values that
// genuinely came from the imported file.
const setAusMismoPrefillAction = ({ duCasefileId, lpAusKey }) => ({
  type: AUS_ACTION_TYPE.SET_AUS_MISMO_PREFILL,
  payload: { duCasefileId: duCasefileId ?? null, lpAusKey: lpAusKey ?? null },
});

// Stamp a receive-time fallback createdDate so every op is orderable even when the source
// (a live SignalR event, or the freshly-created retry op) carries no timestamp; a real
// createdDate on `data` always wins so a page-reload GET keeps the backend's true ordering.
const addNewOperationAction = data => ({
  type: AUS_ACTION_TYPE.ADD_NEW_AUS_SUBMIT_OPERATION,
  payload: { createdDate: new Date().toISOString(), ...data },
});

export const markAusSubmitOperationStartedAction = ({
  operationId,
  loanId,
  companyId,
  isPendingChangesRun = false,
}) =>
  addNewOperationAction({
    operationId,
    ausOperationId: operationId,
    loanId,
    companyId,
    ausOperationStatus: AusOperationStatus.NEW,
    error: null,
    duCasefileId: null,
    lpAusKey: null,
    isPendingChangesRun,
  });

export const processAusSubmitOperationChangedAction = data => (dispatch, getState) => {
  const state = getState();
  const {
    status,
    loanAusStatus,
    error,
    completedDescription,
    description,
    ausLoanSummary,
    options,
    loanId,
  } = data;

  const { applicationId } = selectDealFromApplication(state);

  // One consolidated per-provider message (e.g. "DU: complete, LP: error") drives both the error chip
  // and the terminal toast. The live SignalR payload exposes it under `description` (SkynetAPI forwards
  // AusRunSummary.Describe); `completedDescription` / `error` are the GET-DTO and legacy field fallbacks.
  const notificationMessage = description ?? completedDescription ?? error ?? null;

  // The same terminal event can arrive more than once (SignalR redelivery / a page-reload GET that
  // already recorded this operation), which otherwise shows a duplicate toast. Skip the toast when this
  // operation already sits at the same terminal status; the data updates below stay idempotent.
  const isDuplicateTerminalEvent =
    selectAusSubmitOperations(state)[data.operationId]?.ausOperationStatus === status;

  // The live SignalR event nests the submitted casefile IDs under `options.*` (verified against
  // SkynetAUS AusSubmitOperationEvent, which carries AusOptions { DuCasefileId, LpAusKey }); mirror
  // them onto the operation record so a live-added op is structurally uniform with a GET-fetched op.
  // In this session's own-submit + reload flows these do not drive selectAusCasefileIntake (the
  // working intake wins, then the GET replaces submitOperations wholesale — aus.reducer.js). They
  // DO feed the per-field fallback when a foreign-session op (another tab/user) arrives live and
  // this session's intake is empty — do not remove them.
  const operation = {
    ...data,
    ausOperationStatus: data.status,
    ausOperationId: data.operationId,
    error: notificationMessage,
    duCasefileId: options?.duCasefileId ?? null,
    lpAusKey: options?.lpAusKey ?? null,
  };

  if (
    _.get(options, ['pendingChangesLoadScope', 'type']) === 'All' &&
    _.get(options, ['pendingChangesLoadScope', 'withPendingChanges'])
  ) {
    dispatch(addNewOperationAction({ ...operation, isPendingChangesRun: true }));

    return;
  }

  switch (status) {
    case AusOperationStatus.NEW:
    case AusOperationStatus.IN_PROGRESS:
    case AusOperationStatus.CANCELLED:
      dispatch(addNewOperationAction(operation));
      break;

    case AusOperationStatus.ERROR:
      batch(() => {
        dispatch(addNewOperationAction(operation));
        dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));

        // A partial (mixed-outcome) error also carries the summary of the leg(s) that
        // succeeded — apply it so a succeeded leg shows its success chip alongside the
        // failed leg's error chip (symmetric with the COMPLETED branch).
        if (ausLoanSummary) {
          dispatch(updateAusRelatedDataAction({ ...loanAusStatus }));
          dispatch({
            type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA,
            payload: { loanId, ausSummary: ausLoanSummary },
          });
        }

        if (!isDuplicateTerminalEvent) {
          dispatch(showMessageAction({ message: notificationMessage, variant: 'error' }));
        }
      });
      break;

    case AusOperationStatus.COMPLETED:
      batch(() => {
        dispatch(addNewOperationAction(operation));
        dispatch(updateAusRelatedDataAction({ ...loanAusStatus }));
        dispatch({
          type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA,
          payload: { loanId, ausSummary: ausLoanSummary },
        });

        if (!isDuplicateTerminalEvent && !_.isNil(notificationMessage)) {
          dispatch(showMessageAction({ message: notificationMessage, variant: 'success' }));
        }
      });
      break;

    default:
      break;
  }
};

export const processAusSwitchStatusChangedAction = data => dispatch => {
  const { name, ausStatus } = data;

  switch (name) {
    case AusTypeSwitchStatus.CREATED:
      dispatch({
        type: AUS_ACTION_TYPE.SWITCH_AUS_TYPE_PENDING_STATUS,
      });
      break;

    case AusTypeSwitchStatus.COMPLETED:
      dispatch(updateRootAusRelatedFieldsAction(ausStatus));
      dispatch(updateAusRelatedDataAction(ausStatus));
      dispatch({
        type: AUS_ACTION_TYPE.SWITCH_AUS_TYPE_FULFILLED_STATUS,
      });
      break;

    default:
      break;
  }
};

const fetchAusSummary = params => async dispatch => {
  const { data } = await ausFindingsApi.getAusSummary(params);

  return dispatch({
    type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA,
    payload: { loanId: params.loanId, ausSummary: data },
  });
};

export const getAusSummaryAction = params =>
  catchAsyncActionError(fetchAusSummary)({
    ...params,
    pendingAction: ({ loanId }) => ({
      type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA_PENDING_STATUS,
      payload: { loanId },
    }),
    rejectedAction: ({ loanId }) => ({
      type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA_REJECTED_STATUS,
      payload: { loanId },
    }),
  });

export const refreshAusSummaryAction = params =>
  catchAsyncActionError(fetchAusSummary)({
    ...params,
    silent: true,
    rejectedAction: ({ loanId }) => ({
      type: AUS_ACTION_TYPE.GET_AUS_SUMMARY_DATA_REJECTED_STATUS,
      payload: { loanId },
    }),
  });

export const getAusFindingsListAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await ausFindingsApi.getAusFindingsList(params);

  return dispatch({
    type: AUS_ACTION_TYPE.GET_AUS_FINDINGS_LIST,
    payload: {
      typeOfAus: params.typeOfAus,
      data,
    },
  });
});

export const resetAusFindingsListAction = () => ({
  type: AUS_ACTION_TYPE.RESET_AUS_FINDINGS_LIST,
});

export const getAusSubmitOperationsAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await ausSubmitApi.getAusSubmitOperations(params);

  return dispatch({
    type: AUS_ACTION_TYPE.GET_AUS_SUBMIT_OPERATIONS,
    payload: data.reduce((acc, operation) => {
      const normalizedOperation = {
        operationId: operation.ausOperationId,
        loanId: operation.loanId,
        companyId: operation.companyId,
        ausOperationStatus: AusOperationStatusToNumberStatusMap[operation.ausOperationStatus],
        // Ordering key for selectLatestAusSubmitOperation — the GET returns operations in
        // unspecified SQL order, so "latest" is resolved by createdDate, not array position.
        createdDate: operation.createdDate ?? null,
        // The backend's actionable per-leg message ("Du: … | Lp: …") — without this, an ERROR
        // operation fetched via GET (page reload, or anyone who missed the live SignalR push)
        // would only ever surface the generic fallback copy instead of the real failure reason.
        error: operation.completedDescription ?? null,
        // The casefile IDs this operation was submitted with — the source selectAusCasefileIntake
        // rehydrates from after a reload (the typed values are otherwise lost), so the error-state
        // Retry re-dispatches the true last-submitted IDs. Legacy ops predate the columns → null.
        duCasefileId: operation.duCasefileId ?? null,
        lpAusKey: operation.lpAusKey ?? null,
      };

      acc[normalizedOperation.operationId] = normalizedOperation;

      return acc;
    }, {}),
  });
});

export const getAusCasefilePrefillAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await ausSubmitApi.getMismoImportCasefile(params);
  const prefillIds = { duCasefileId: data?.duCasefileId, lpAusKey: data?.lpAusKey };

  // mismoPrefill drives the provenance chip; casefileIntake seeds the drawer fields + the
  // collapsed chips and remains the retry source, so both are written from the fetch.
  return batch(() => {
    dispatch(setAusMismoPrefillAction(prefillIds));
    dispatch(setAusCasefileIntakeAction(prefillIds));
  });
});

export const startWholesaleAusSubmitAction = catchAsyncActionError(
  ({ companyId, loanId, duCasefileId, lpAusKey }) =>
    async dispatch => {
      const { data } = await ausSubmitApi.startAusSubmit({
        companyId,
        loanId,
        duCasefileId,
        lpAusKey,
        pendingChangesLoadScope: WHOLESALE_AUS_LOAD_SCOPE,
      });

      const operationId = _.isString(data) ? data : data?.operationId;

      return batch(() => {
        dispatch(setAusCasefileIntakeAction({ duCasefileId, lpAusKey }));

        if (operationId) {
          dispatch(
            addNewOperationAction({
              operationId,
              loanId,
              companyId,
              ausOperationStatus: AusOperationStatus.NEW,
            }),
          );
        }
      });
    },
);
